import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, IconButton } from "@mui/material";

import KeyboardArrowLeft from "../../components/icon-components/KeyboardArrowLeft";
import CustomChip from "../../components/custom-chip/CustomChip";
import MilestoneContentForm from "../../components/milestone-content-form/MilestoneContentForm";
import {
  PAGE_HEADER_EDIT_MILESTONE_CONTENT,
  TAB_LABEL_ACTIVITY_HISTORY,
  TAB_LABEL_EDUCATION_CONTENT,
  TAB_LABEL_MILESTONE_DETAILS,
} from "../../utility/constants/locale";
import { useGetMilestoneTaskQuery } from "./GetMilestoneTaskApiSlice";
import CustomTab from "../../components/custom-tab/CustomTab";
import "./EditMilestone.scss";
import {
  EDIT_MILESTONE_TABS,
  LANGUAGE_NAME_ENGLISH,
  MILESTONE_MESSAGES,
} from "../../utility/constants/common-constants";
import CustomSpinner from "../../components/custom-spinner/CustomSpinner";
import EducationContentForm from "../../components/education-content-form/EducationContentForm";
import { useUpdateMilestoneTaskMutation } from "./UpdateMilestoneTaskApiSlice";
import { useSnackbar } from "notistack";
import { useAppSelector } from "../../store/hooks";
import { activeLanguages } from "../../constants/TestData";

const EditMilestone: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams<{ id: string }>();
  const userName = useAppSelector((state) => state.adminAuth.user?.name);

  const accessToken = useAppSelector(
    (state) => state.adminAuth.user?.accessToken!
  );
  const {
    data: milestoneDetails,
    isLoading,
    error,
  } = useGetMilestoneTaskQuery({ accessToken, milestoneTaskId: id as string });

  const { milestone } = milestoneDetails || {};
  const [activeTab, setActiveTab] = useState("");
  const [hideTabs, setHideTabs] = useState<string[]>([]);
  const displayGroups = useAppSelector(
    (state) => state.adminMasterData.displayGroups
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateMilestoneTask] = useUpdateMilestoneTaskMutation();

  const initialValues = {
    isMulticode: milestone?.isMulticode || false,
    milestoneTypeId: milestone?.milestoneTypeId || 0,
    displayGroupId: milestone?.displayGroupId || 0,
    statusId: milestone?.statusId || 0,
    milestoneDisplayName: milestone?.displayName || "",
    sourceMilestoneCode: milestone?.sourceMilestoneCode || "",
    description: milestone?.longDescription || "",
    rewardPoints: milestone?.rewardPoints || 0,
    educationalContentTypeId: milestone?.educationalContentTypeId || 0,
    milestoneLearningUrls: milestone?.milestoneLearningUrls || "",
  };

  const [translations, setTranslations] = useState<
    {
      milestoneTranslationId: number;
      milestoneId: number;
      languageId: number;
      displayName: string;
      longDescription: string;
    }[]
  >([]);

  const [languageIds, setLanguageIds] = useState<number[]>([]);

  const [formValues, setFormValues] = useState(initialValues);

  useEffect(() => {
    if (milestone?.milestoneTypeId === 3) {
      setActiveTab(TAB_LABEL_EDUCATION_CONTENT);
      setHideTabs([TAB_LABEL_MILESTONE_DETAILS]);
    } else {
      setActiveTab(TAB_LABEL_MILESTONE_DETAILS);
      setHideTabs([TAB_LABEL_EDUCATION_CONTENT]);
    }
  }, [milestone]);

  useEffect(() => {
    if (milestone) {
      setFormValues({
        isMulticode: milestone?.isMulticode || false,
        milestoneTypeId: milestone?.milestoneTypeId || 0,
        displayGroupId: milestone?.displayGroupId || 0,
        statusId: milestone?.statusId || 0,
        milestoneDisplayName: milestone?.milestoneDisplayName || "",
        sourceMilestoneCode: milestone?.sourceMilestoneCode || "",
        description: milestone?.longDescription || "",
        rewardPoints: milestone?.rewardPoints || 0,
        educationalContentTypeId: milestone?.educationalContentTypeId || 0,
        milestoneLearningUrls: milestone?.milestoneLearningUrls || "",
      });
      setTranslations(milestone.translations);
      setLanguageIds(() => {
        const updatedIds = milestone.languageIds
          ? [...milestone.languageIds]
          : [];
        const englishLanguageId = activeLanguages.find(
          (lang) => lang.languageName.toLowerCase() === LANGUAGE_NAME_ENGLISH
        )?.languageId;
        if (englishLanguageId && !updatedIds.includes(englishLanguageId)) {
          updatedIds.push(englishLanguageId);
        }
        return updatedIds;
      });
    }
  }, [milestone]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const handleFormSubmit = async (values: any) => {
    setIsUpdating(true);

    try {
      const body = {
        milestoneId: Number(id),
        milestoneTypeId: values.milestoneTypeId,
        displayGroupId: values.displayGroupId,
        statusId: values.statusId,
        milestoneDisplayName: values.milestoneDisplayName,
        description: values.description,
        rewardPoints: values.rewardPoints,
        updatedBy: userName || "system",
        translations: values.translations,
      };
      await updateMilestoneTask({ body, accessToken }).unwrap();
      enqueueSnackbar(
        activeTab === TAB_LABEL_MILESTONE_DETAILS
          ? MILESTONE_MESSAGES.EditMilestone.carePlan.Success
          : MILESTONE_MESSAGES.EditMilestone.education.Success,
        {
          variant: "success",
        }
      );
      navigate("/admin");
    } catch (error) {
      enqueueSnackbar(
        activeTab === TAB_LABEL_MILESTONE_DETAILS
          ? MILESTONE_MESSAGES.EditMilestone.carePlan.Fail
          : MILESTONE_MESSAGES.EditMilestone.education.Fail,
        { variant: "error" }
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const isMilestoneInUse = milestone?.isMilestoneInUse || false; // Determine if the milestone is in use

  if (isLoading) return <CustomSpinner />;

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        mt={3}
      >
        <Typography
          variant="h6"
          color="error"
        >
          Error loading milestone task.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      p={3}
      className={"editMilestoneContainer"}
    >
      <Box
        display="flex"
        alignItems="center"
        mb={2}
      >
        <IconButton
          onClick={() => navigate("/admin")}
          aria-label="back button"
        >
          <KeyboardArrowLeft />
        </IconButton>
        <Typography variant="h2">
          {PAGE_HEADER_EDIT_MILESTONE_CONTENT}
        </Typography>
      </Box>
      <Box
        display="flex"
        alignItems="center"
        borderBottom="1px solid #BCA685"
        justifyContent="space-between"
        mb={2}
      >
        <CustomTab
          value={activeTab}
          hiddenTabs={hideTabs}
          handleTabChange={handleTabChange}
          tabs={EDIT_MILESTONE_TABS}
        />
        <CustomChip
          label={
            isMilestoneInUse
              ? "Milestone currently in use"
              : "Milestone not in use"
          }
          color={isMilestoneInUse ? "warning" : "neutral"}
        />
      </Box>

      {activeTab === TAB_LABEL_MILESTONE_DETAILS && (
        <MilestoneContentForm
          initialValues={formValues}
          onSubmit={handleFormSubmit}
          onSaveDraft={handleFormSubmit}
          isEditMode={true}
          isMilestoneInUse={isMilestoneInUse}
          milestoneData={displayGroups}
          translations={translations}
          languageIds={languageIds}
        />
      )}
      {activeTab === TAB_LABEL_EDUCATION_CONTENT && (
        <EducationContentForm
          initialValues={formValues}
          onSubmit={handleFormSubmit}
          onSaveDraft={handleFormSubmit}
          isEditMode={true}
          isMilestoneInUse={isMilestoneInUse}
          milestoneData={displayGroups}
          translations={translations}
          languageIds={languageIds}
        />
      )}
      {activeTab === TAB_LABEL_ACTIVITY_HISTORY && (
        <Box mt={3}>
          <Typography variant="h6">Activity History</Typography>
          {/* Add activity history content here */}
        </Box>
      )}
      {isUpdating && <CustomSpinner />}
    </Box>
  );
};

export default EditMilestone;
